import { create } from 'zustand';
import { BELeague } from '@/lib/leagueMappers';
import {
  fetchMyLeagues,
  fetchLeagueByCode as fetchLeagueByCodeApi,
  joinLeague,
  createLeague as createLeagueApi,
  fetchTeams,
  getErrorMessage,
  fetchCurrentTournamentCalendar,
} from '@/lib/network';
import type { TournamentCalendar, CreateLeaguePayload } from '@/lib/network';
import { getUserSessionGeneration } from '@/lib/userSessionGeneration';

export type { CreateLeaguePayload };

// Matches the backend's maximum `limit` for GET /api/league/me.
const MY_LEAGUES_PAGE_SIZE = 100;

interface LeaguesState {
  leagues: BELeague[];
  loading: boolean;
  error: string | number | null;
  joining: boolean;
  creating: boolean;
  fetchLeagues: () => Promise<void>;
  getLeagueByCode: (code: string) => Promise<BELeague>;
  joinLeagueByCode: (code: string, userId: string) => Promise<string>;
  createLeague: (payload: CreateLeaguePayload) => Promise<string>;
}

export const fetchActiveTournamentCalendar = async (): Promise<TournamentCalendar | null> => {
  const res = await fetchCurrentTournamentCalendar();
  return res.data ?? null;
};

export const useLeaguesStore = create<LeaguesState>(set => ({
  leagues: [],
  loading: false,
  error: null,
  joining: false,
  creating: false,

  getLeagueByCode: async (code: string) => {
    try {
      const res = await fetchLeagueByCodeApi(code);
      return res.data;
    } catch (error) {
      set({ error: error.status, loading: false });
      throw error;
    }
  },

  fetchLeagues: async () => {
    const sessionGeneration = getUserSessionGeneration();
    set({ loading: true, error: null });
    try {
      // The API caps a page at MY_LEAGUES_PAGE_SIZE, so walk every page - a single
      // request silently drops everything past the first 100 leagues.
      const leaguesById = new Map<string, BELeague>();
      let page = 1;
      let totalPages = 1;
      do {
        const res = await fetchMyLeagues({ page, limit: MY_LEAGUES_PAGE_SIZE });
        if (sessionGeneration !== getUserSessionGeneration()) return;
        // Keyed by id: a league joined mid-walk shifts the ordering and would
        // otherwise repeat one row across a page boundary.
        for (const league of res.data.items) leaguesById.set(league.id, league);
        totalPages = res.data.pagination.totalPages;
        page += 1;
      } while (page <= totalPages);
      set({ leagues: [...leaguesById.values()], loading: false });
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) return;
      set({ error: getErrorMessage(e, 'Failed to fetch leagues'), loading: false });
    }
  },

  joinLeagueByCode: async (code: string, userId: string) => {
    const sessionGeneration = getUserSessionGeneration();
    set({ joining: true, error: null });
    const teamsRes = await fetchTeams({ userId }).catch((e: unknown) => {
      if (sessionGeneration !== getUserSessionGeneration()) throw e;
      set({ joining: false, error: getErrorMessage(e) });
      throw e;
    });
    if (sessionGeneration !== getUserSessionGeneration()) {
      throw new Error('User session changed while joining the league');
    }
    const fantasyTeam = teamsRes.data.items[0];
    if (!fantasyTeam) {
      const err = new Error('You need a fantasy team before joining a league');
      set({ joining: false, error: err.message });
      throw err;
    }
    try {
      const joinRes = await joinLeague({ code, user_id: userId, fantasy_team_id: fantasyTeam.id });
      if (sessionGeneration !== getUserSessionGeneration()) {
        throw new Error('User session changed while joining the league');
      }
      set({ joining: false });
      return joinRes.data.league.id;
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) throw e;
      set({ joining: false, error: getErrorMessage(e) });
      throw e;
    }
  },

  createLeague: async (payload: CreateLeaguePayload) => {
    const sessionGeneration = getUserSessionGeneration();
    set({ creating: true, error: null });
    try {
      const res = await createLeagueApi(payload);
      if (sessionGeneration !== getUserSessionGeneration()) {
        throw new Error('User session changed while creating the league');
      }
      set({ creating: false });
      return res.data.id;
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) throw e;
      set({ creating: false, error: getErrorMessage(e) });
      throw e;
    }
  },
}));
